import {
  ROOT_JOURNAL_URL,
  ROOT_EDITORS_STAFF_URL,
} from '@/shared/constants/urlConstants';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';
import { logError, normalizeError } from '@/shared/lib/errors';
import type { Breadcrumb } from '@/shared/ui/AppBreadcrumbs/model/types/appBreadcrumbs';
import type { IChiefEditor, IJournalArticle } from '@/entities/journal/model/types';
import type { IPublication } from '@/entities/publication/model/types/publication';
import { fetchChiefEditorProfile } from '@/entities/journal/lib/fetchChiefEditorProfile';

const ARTICLES_PER_PAGE = 6;
const ARTICLES_FIRST_PAGE = 1;
const CHIEF_EDITOR_NAME = 'Игорь Лесовских';

interface EditorDetailPayload {
  chiefEditor: Partial<IChiefEditor>;
  articles: IJournalArticle[];
  articlesTotal: number;
  publications: IPublication[];
}

export const useEditorDetail = () => {
  const { $journalService, $publicationService } = useNuxtApp();

  const articlesPage = ref(ARTICLES_FIRST_PAGE);

  const breadcrumbs: Breadcrumb[] = [
    { id: 1, text: 'Главная', to: '/' },
    { id: 2, text: 'Редакция', to: ROOT_EDITORS_STAFF_URL },
    { id: 3, text: CHIEF_EDITOR_NAME },
  ];

  const fetchArticles = async (
    userId: string | number,
    page: number,
  ): Promise<{ list: IJournalArticle[]; total: number }> => {
    const response = await $journalService.getArticles<IJournalArticle[]>({
      per_page: ARTICLES_PER_PAGE,
      page,
      'sort-created_at': 'desc',
      user_id: userId,
    });
    const list = Array.isArray(response.data) ? response.data : [];
    return { list, total: response.meta?.total ?? list.length };
  };

  const fetchPublications = async (userId: string | number): Promise<IPublication[]> => {
    try {
      const { data } = await $publicationService.getPublications({ user_id: userId });
      return data ?? [];
    } catch (error) {
      logError(normalizeError(error), { action: 'editor-publications' });
      return [];
    }
  };

  const pageAsyncData = useAsyncData<EditorDetailPayload>('editor-detail', async () => {
    try {
      const chiefEditor = await fetchChiefEditorProfile(
        $journalService,
        'editor-detail:topics',
      );
      if (!chiefEditor.id) {
        return { chiefEditor, articles: [], articlesTotal: 0, publications: [] };
      }
      const [{ list, total }, publications] = await Promise.all([
        fetchArticles(chiefEditor.id, ARTICLES_FIRST_PAGE),
        fetchPublications(chiefEditor.id),
      ]);
      return { chiefEditor, articles: list, articlesTotal: total, publications };
    } catch (error) {
      logError(normalizeError(error), { action: 'editor-detail-page' });
      throw createError({ statusCode: getErrorStatus(error), fatal: true });
    }
  });

  const { data, status, error } = pageAsyncData;

  watchEffect(() => {
    if (error.value) {
      showError(createError({ statusCode: getErrorStatus(error.value), fatal: true }));
    }
  });

  const chiefEditor: ComputedRef<Partial<IChiefEditor>> = computed(
    () => data.value?.chiefEditor ?? {},
  );
  const articles: ComputedRef<IJournalArticle[]> = computed(
    () => data.value?.articles ?? [],
  );
  const publications: ComputedRef<IPublication[]> = computed(
    () => data.value?.publications ?? [],
  );
  const isChiefEditorLoading: ComputedRef<boolean> = computed(
    () => status.value === 'pending',
  );
  const isShowMoreArticles: ComputedRef<boolean> = computed(
    () => articles.value.length < (data.value?.articlesTotal ?? 0),
  );

  const loadMoreArticles = async (): Promise<void> => {
    const editorId = chiefEditor.value.id;
    if (!editorId || !data.value) return;
    articlesPage.value += 1;
    const { list, total } = await fetchArticles(editorId, articlesPage.value);
    data.value = {
      ...data.value,
      articles: [...data.value.articles, ...list],
      articlesTotal: total,
    };
  };

  const articleLink = (article: IJournalArticle): string =>
    `${ROOT_JOURNAL_URL}${article.topic?.slug}/${article.slug}/`;

  return {
    pageReady: pageAsyncData,
    pageError: error,
    chiefEditor,
    isChiefEditorLoading,
    articles,
    publications,
    isShowMoreArticles,
    breadcrumbs,
    loadMoreArticles,
    articleLink,
  };
};

export default useEditorDetail;
